import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Select,
  Switch,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Statistic,
  Badge,
  Tabs,
  List,
  Empty,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  WarningOutlined,
  AppstoreOutlined,
  DollarOutlined,
  InboxOutlined,
  GiftOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import * as productService from "./services/productService";
import * as bundleService from "./services/bundleService";

const { Title, Text } = Typography;
const { TextArea } = Input;

const ProductsPage = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("products");
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form] = Form.useForm();

  // Bundle state
  const [isBundleModalOpen, setIsBundleModalOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [bundleForm] = Form.useForm();
  const [bundleItems, setBundleItems] = useState([]);

  // Fetch products
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: productService.getProducts,
  });

  // Fetch bundles
  const { data: bundles = [], isLoading: bundlesLoading } = useQuery({
    queryKey: ["bundles"],
    queryFn: bundleService.getBundles,
  });

  // Create product mutation
  const createMutation = useMutation({
    mutationFn: productService.createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries(["products"]);
      message.success("Product created successfully");
      handleCloseModal();
    },
    onError: (error) => {
      message.error("Failed to create product: " + (error.message || "Unknown error"));
    },
  });

  // Update product mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => productService.updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["products"]);
      message.success("Product updated successfully");
      handleCloseModal();
    },
    onError: (error) => {
      message.error("Failed to update product: " + (error.message || "Unknown error"));
    },
  });

  // Delete product mutation
  const deleteMutation = useMutation({
    mutationFn: productService.deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries(["products"]);
      message.success("Product deleted successfully");
    },
    onError: (error) => {
      message.error("Failed to delete product: " + (error.message || "Unknown error"));
    },
  });

  // Bundle mutations
  const createBundleMutation = useMutation({
    mutationFn: bundleService.createBundle,
    onSuccess: () => {
      queryClient.invalidateQueries(["bundles"]);
      message.success("Bundle created successfully");
      handleCloseBundleModal();
    },
    onError: (error) => {
      message.error("Failed to create bundle: " + (error.message || "Unknown error"));
    },
  });

  const updateBundleMutation = useMutation({
    mutationFn: ({ id, data }) => bundleService.updateBundle(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["bundles"]);
      message.success("Bundle updated successfully");
      handleCloseBundleModal();
    },
    onError: (error) => {
      message.error("Failed to update bundle: " + (error.message || "Unknown error"));
    },
  });

  const deleteBundleMutation = useMutation({
    mutationFn: bundleService.deleteBundle,
    onSuccess: () => {
      queryClient.invalidateQueries(["bundles"]);
      message.success("Bundle deleted successfully");
    },
    onError: (error) => {
      message.error("Failed to delete bundle: " + (error.message || "Unknown error"));
    },
  });

  // Filter products by search term
  const filteredProducts = products.filter((product) =>
    product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter bundles by search term
  const filteredBundles = bundles.filter((bundle) =>
    bundle.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bundle.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate stats
  const totalProducts = products.length;
  const activeProducts = products.filter((p) => p.active !== false).length;
  const lowStockProducts = products.filter((p) =>
    p.trackInventory && p.stockQuantity !== null && p.lowStockThreshold !== null &&
    p.stockQuantity <= p.lowStockThreshold
  ).length;
  const totalInventoryValue = products.reduce((sum, p) => {
    const price = p.wholesalePrice || p.unitPrice || 0;
    const qty = p.stockQuantity || 0;
    return sum + (price * qty);
  }, 0);

  const handleOpenModal = (product = null) => {
    setEditingProduct(product);
    if (product) {
      form.setFieldsValue(product);
    } else {
      form.resetFields();
      form.setFieldsValue({
        active: true,
        trackInventory: true,
        stockQuantity: 0,
        lowStockThreshold: 10,
        packQuantity: 1,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, data: values });
    } else {
      createMutation.mutate(values);
    }
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  // Bundle handlers
  const handleOpenBundleModal = (bundle = null) => {
    setEditingBundle(bundle);
    if (bundle) {
      bundleForm.setFieldsValue({
        name: bundle.name,
        description: bundle.description,
        active: bundle.active !== false,
      });
      setBundleItems(bundle.items?.map(item => ({
        productId: item.product?.id,
        productName: item.product?.name,
        quantity: item.quantity,
        price: item.product?.wholesalePrice || item.product?.unitPrice || 0,
      })) || []);
    } else {
      bundleForm.resetFields();
      bundleForm.setFieldsValue({ active: true });
      setBundleItems([]);
    }
    setIsBundleModalOpen(true);
  };

  const handleCloseBundleModal = () => {
    setIsBundleModalOpen(false);
    setEditingBundle(null);
    bundleForm.resetFields();
    setBundleItems([]);
  };

  const handleBundleSubmit = async (values) => {
    if (bundleItems.length === 0) {
      message.error("Please add at least one product to the bundle");
      return;
    }

    const bundleData = {
      name: values.name,
      description: values.description,
      active: values.active,
      items: bundleItems.map(item => ({
        productId: item.productId,
        quantity: item.quantity,
      })),
    };

    if (editingBundle) {
      updateBundleMutation.mutate({ id: editingBundle.id, data: bundleData });
    } else {
      createBundleMutation.mutate(bundleData);
    }
  };

  const handleDeleteBundle = (id) => {
    deleteBundleMutation.mutate(id);
  };

  const addProductToBundle = (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const existing = bundleItems.find(item => item.productId === productId);
    if (existing) {
      setBundleItems(bundleItems.map(item =>
        item.productId === productId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setBundleItems([...bundleItems, {
        productId: product.id,
        productName: product.name,
        quantity: 1,
        price: product.wholesalePrice || product.unitPrice || 0,
      }]);
    }
  };

  const removeProductFromBundle = (productId) => {
    setBundleItems(bundleItems.filter(item => item.productId !== productId));
  };

  const updateBundleItemQuantity = (productId, quantity) => {
    if (quantity < 1) {
      removeProductFromBundle(productId);
      return;
    }
    setBundleItems(bundleItems.map(item =>
      item.productId === productId ? { ...item, quantity } : item
    ));
  };

  const calculateBundleTotal = () => {
    return bundleItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const columns = [
    {
      title: "Product",
      key: "product",
      render: (_, record) => (
        <div>
          <Text strong>{record.name}</Text>
          {record.sku && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>SKU: {record.sku}</Text>
            </div>
          )}
          {record.size && record.flavor && (
            <div>
              <Tag size="small">{record.size}</Tag>
              <Tag size="small" color="orange">{record.flavor}</Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (category) => category || "-",
    },
    {
      title: "Wholesale Price",
      dataIndex: "wholesalePrice",
      key: "wholesalePrice",
      render: (price) => price ? `$${Number(price).toFixed(2)}` : "-",
      sorter: (a, b) => (a.wholesalePrice || 0) - (b.wholesalePrice || 0),
    },
    {
      title: "Retail Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      render: (price) => price ? `$${Number(price).toFixed(2)}` : "-",
      sorter: (a, b) => (a.unitPrice || 0) - (b.unitPrice || 0),
    },
    {
      title: "Stock",
      key: "stock",
      render: (_, record) => {
        if (!record.trackInventory) {
          return <Text type="secondary">Not tracked</Text>;
        }
        const isLowStock = record.stockQuantity <= record.lowStockThreshold;
        return (
          <Badge
            status={isLowStock ? "warning" : "success"}
            text={
              <span style={{ color: isLowStock ? "#faad14" : "#52c41a" }}>
                {record.stockQuantity || 0}
                {isLowStock && <WarningOutlined style={{ marginLeft: 4 }} />}
              </span>
            }
          />
        );
      },
      sorter: (a, b) => (a.stockQuantity || 0) - (b.stockQuantity || 0),
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (active) => (
        <Tag color={active !== false ? "green" : "red"}>
          {active !== false ? "Active" : "Inactive"}
        </Tag>
      ),
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      onFilter: (value, record) => (record.active !== false) === value,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleOpenModal(record)}
          />
          <Popconfirm
            title="Delete this product?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Render Products Tab
  const renderProductsTab = () => (
    <>
      {/* Stats Row */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Total Products"
              value={totalProducts}
              prefix={<AppstoreOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Active Products"
              value={activeProducts}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Low Stock"
              value={lowStockProducts}
              valueStyle={{ color: lowStockProducts > 0 ? "#faad14" : "#52c41a" }}
              prefix={lowStockProducts > 0 ? <WarningOutlined /> : <InboxOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="Inventory Value"
              value={totalInventoryValue}
              precision={2}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Actions Bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <Input
            placeholder="Search products..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: 300 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal()}
          >
            Add Product
          </Button>
        </div>
      </Card>

      {/* Products Table - Desktop */}
      <div className="hidden md:block">
        <Card size="small">
          <Table
            columns={columns}
            dataSource={filteredProducts}
            rowKey="id"
            loading={isLoading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${total} products`,
            }}
          />
        </Card>
      </div>

      {/* Products Cards - Mobile */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <Card size="small" loading={true} />
        ) : filteredProducts.length > 0 ? (
          filteredProducts.map((product) => {
            const isLowStock = product.trackInventory && product.stockQuantity <= product.lowStockThreshold;
            return (
              <Card key={product.id} size="small" className="shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <Text strong className="text-base">{product.name}</Text>
                    {product.sku && (
                      <div>
                        <Text type="secondary" style={{ fontSize: 11 }}>SKU: {product.sku}</Text>
                      </div>
                    )}
                  </div>
                  <Tag color={product.active !== false ? "green" : "red"} style={{ marginLeft: 8 }}>
                    {product.active !== false ? "Active" : "Inactive"}
                  </Tag>
                </div>
                <Row gutter={16} className="mb-3">
                  <Col span={12}>
                    <div className="text-xs text-gray-500">Wholesale</div>
                    <div className="text-base font-semibold text-green-600">
                      {product.wholesalePrice ? `$${Number(product.wholesalePrice).toFixed(2)}` : "-"}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="text-xs text-gray-500">Retail</div>
                    <div className="text-base font-medium">
                      {product.unitPrice ? `$${Number(product.unitPrice).toFixed(2)}` : "-"}
                    </div>
                  </Col>
                </Row>
                <div className="flex gap-2 pt-2">
                  <Button type="primary" icon={<EditOutlined />} onClick={() => handleOpenModal(product)} className="flex-1" size="middle">Edit</Button>
                  <Popconfirm title="Delete this product?" onConfirm={() => handleDelete(product.id)} okText="Delete" okButtonProps={{ danger: true }}>
                    <Button danger icon={<DeleteOutlined />} className="flex-1" size="middle">Delete</Button>
                  </Popconfirm>
                </div>
              </Card>
            );
          })
        ) : (
          <Card size="small">
            <div className="text-center py-8 text-gray-500">No products found</div>
          </Card>
        )}
      </div>
    </>
  );

  // Render Bundles Tab
  const renderBundlesTab = () => (
    <>
      {/* Stats Row */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic
              title="Total Bundles"
              value={bundles.length}
              prefix={<GiftOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small">
            <Statistic
              title="Active Bundles"
              value={bundles.filter(b => b.active !== false).length}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Total Products in Bundles"
              value={bundles.reduce((sum, b) => sum + (b.items?.reduce((s, i) => s + (i.quantity || 0), 0) || 0), 0)}
              prefix={<AppstoreOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Actions Bar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <Input
            placeholder="Search bundles..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: 300 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenBundleModal()}
          >
            Create Bundle
          </Button>
        </div>
      </Card>

      {/* Bundles List */}
      {bundlesLoading ? (
        <Card size="small" loading={true} />
      ) : filteredBundles.length > 0 ? (
        <div className="space-y-3">
          {filteredBundles.map((bundle) => {
            const totalItems = bundle.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
            const totalPrice = bundle.items?.reduce((sum, item) => {
              const price = item.product?.wholesalePrice || item.product?.unitPrice || 0;
              return sum + (price * (item.quantity || 0));
            }, 0) || 0;

            return (
              <Card key={bundle.id} size="small" className="shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <GiftOutlined style={{ color: "#722ed1" }} />
                      <Text strong className="text-base">{bundle.name}</Text>
                      <Tag color={bundle.active !== false ? "green" : "red"}>
                        {bundle.active !== false ? "Active" : "Inactive"}
                      </Tag>
                    </div>
                    {bundle.description && (
                      <Text type="secondary" className="text-sm block mt-1">{bundle.description}</Text>
                    )}
                  </div>
                </div>

                {/* Bundle Items */}
                <div className="bg-gray-50 rounded p-2 mb-3">
                  <div className="text-xs text-gray-500 mb-2">Bundle Contents ({totalItems} items)</div>
                  <div className="space-y-1">
                    {bundle.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span>{item.product?.name || "Unknown"} x{item.quantity}</span>
                        <span className="text-gray-600">
                          ${((item.product?.wholesalePrice || item.product?.unitPrice || 0) * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-semibold">
                    <span>Bundle Total:</span>
                    <span className="text-green-600">${totalPrice.toFixed(2)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button type="primary" icon={<EditOutlined />} onClick={() => handleOpenBundleModal(bundle)} className="flex-1" size="middle">Edit</Button>
                  <Popconfirm title="Delete this bundle?" onConfirm={() => handleDeleteBundle(bundle.id)} okText="Delete" okButtonProps={{ danger: true }}>
                    <Button danger icon={<DeleteOutlined />} className="flex-1" size="middle">Delete</Button>
                  </Popconfirm>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card size="small">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No bundles created yet"
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenBundleModal()}>
              Create Your First Bundle
            </Button>
          </Empty>
        </Card>
      )}
    </>
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Products & Bundles</Title>
        <Text type="secondary">Manage your product catalog, inventory, and wholesale bundles</Text>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          setSearchTerm("");
        }}
        items={[
          {
            key: "products",
            label: (
              <span>
                <AppstoreOutlined />
                Products ({products.length})
              </span>
            ),
            children: renderProductsTab(),
          },
          {
            key: "bundles",
            label: (
              <span>
                <GiftOutlined />
                Bundles ({bundles.length})
              </span>
            ),
            children: renderBundlesTab(),
          },
        ]}
      />

      {/* Product Modal */}
      <Modal
        title={editingProduct ? "Edit Product" : "Add Product"}
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={700}
        styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item name="name" label="Product Name" rules={[{ required: true, message: "Please enter product name" }]}>
                <Input placeholder="e.g., BBQ Brisket Jerky 3oz" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="sku" label="SKU">
                <Input placeholder="e.g., BM-BBQ-3OZ" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Product description" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="category" label="Category">
                <Select placeholder="Select category" allowClear>
                  <Select.Option value="Jerky">Jerky</Select.Option>
                  <Select.Option value="Bundle">Bundle</Select.Option>
                  <Select.Option value="Snacks">Snacks</Select.Option>
                  <Select.Option value="Other">Other</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="size" label="Size">
                <Select placeholder="Select size" allowClear>
                  <Select.Option value="3oz">3oz</Select.Option>
                  <Select.Option value="6oz">6oz</Select.Option>
                  <Select.Option value="12oz">12oz</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="flavor" label="Flavor">
                <Select placeholder="Select flavor" allowClear>
                  <Select.Option value="BBQ Brisket">BBQ Brisket</Select.Option>
                  <Select.Option value="Teriyaki">Teriyaki</Select.Option>
                  <Select.Option value="Original">Original</Select.Option>
                  <Select.Option value="Spicy">Spicy</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="wholesalePrice" label="Wholesale Price" rules={[{ required: true, message: "Please enter wholesale price" }]}>
                <InputNumber prefix="$" min={0} precision={2} style={{ width: "100%" }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="unitPrice" label="Retail Price">
                <InputNumber prefix="$" min={0} precision={2} style={{ width: "100%" }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="compareAtPrice" label="Compare at Price">
                <InputNumber prefix="$" min={0} precision={2} style={{ width: "100%" }} placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} sm={8}>
              <Form.Item name="trackInventory" label="Track Inventory" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="stockQuantity" label="Stock Quantity">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="lowStockThreshold" label="Low Stock Alert">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} sm={8}>
              <Form.Item name="packQuantity" label="Pack Quantity">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="weight" label="Weight (oz)">
                <InputNumber min={0} precision={2} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {editingProduct ? "Update" : "Create"}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Bundle Modal */}
      <Modal
        title={editingBundle ? "Edit Bundle" : "Create Bundle"}
        open={isBundleModalOpen}
        onCancel={handleCloseBundleModal}
        footer={null}
        width={700}
        styles={{ body: { maxHeight: "80vh", overflowY: "auto" } }}
      >
        <Form form={bundleForm} layout="vertical" onFinish={handleBundleSubmit} style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item name="name" label="Bundle Name" rules={[{ required: true, message: "Please enter bundle name" }]}>
                <Input placeholder="e.g., Variety Pack - 12 Bags" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Bundle description (optional)" />
          </Form.Item>

          {/* Add Products to Bundle */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Add Products to Bundle</label>
            <Select
              placeholder="Search and select products..."
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={products.map(p => ({
                value: p.id,
                label: `${p.name} - $${(p.wholesalePrice || p.unitPrice || 0).toFixed(2)}`,
              }))}
              onSelect={addProductToBundle}
              style={{ width: "100%" }}
              value={null}
            />
          </div>

          {/* Bundle Items */}
          {bundleItems.length > 0 ? (
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Bundle Contents ({bundleItems.reduce((sum, item) => sum + item.quantity, 0)} items)
              </div>
              <List
                size="small"
                dataSource={bundleItems}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<MinusCircleOutlined />}
                        onClick={() => removeProductFromBundle(item.productId)}
                      />,
                    ]}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <span className="flex-1">{item.productName}</span>
                      <InputNumber
                        size="small"
                        min={1}
                        value={item.quantity}
                        onChange={(val) => updateBundleItemQuantity(item.productId, val)}
                        style={{ width: 60 }}
                      />
                      <span className="text-gray-500 w-20 text-right">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  </List.Item>
                )}
              />
              <div className="border-t border-gray-200 mt-2 pt-2 flex justify-between font-semibold">
                <span>Bundle Total:</span>
                <span className="text-green-600">${calculateBundleTotal().toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-6 mb-4 text-center text-gray-500">
              <GiftOutlined style={{ fontSize: 32, marginBottom: 8, display: "block" }} />
              No products added yet. Use the dropdown above to add products.
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button onClick={handleCloseBundleModal}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={createBundleMutation.isPending || updateBundleMutation.isPending}
              disabled={bundleItems.length === 0}
            >
              {editingBundle ? "Update Bundle" : "Create Bundle"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductsPage;
